import { useState } from 'react';

import { useClickAway } from '@uidotdev/usehooks';
import { Download, FileCheck, FileText, Highlighter, Loader2 } from 'lucide-react';

import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDownloadDocument } from '@/api/mutations/useDownloadDocument';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/atoms/Tooltip';
import { cn } from '@/helpers/shadCnUtils';

export const DownloadIcon = ({
  caseId,
  docId,
  fileName,
  isProcessed,
  fileExtension,
}: {
  caseId: string;
  docId: string;
  fileName?: string;
  isProcessed?: boolean;
  fileExtension?: string;
}) => {
  const [downloadingHighlighted, setDownloadingHighlighted] = useState<boolean>(false);
  const [downloadingAsPDF, setDownloadingAsPDF] = useState<boolean>(false);
  const [showOptions, setShowOptions] = useState(false);
  const containerRef = useClickAway<HTMLDivElement>(() => setShowOptions(false));

  const { mutate: downloadOriginal, isLoading: isDownloadingOriginal } = useDownloadDocument({
    caseId,
    docId,
    fileName,
    downloadType: 'original',
  });

  const { mutateAsync: downloadHighlighted, isLoading: isDownloadingHighlighted } = useDownloadDocument({
    caseId,
    docId,
    fileName,
    downloadType: 'highlighted',
  });

  const { mutateAsync: downloadAsPDF, isLoading: isDownloadingAsPDF } = useDownloadDocument({
    caseId,
    docId,
    fileName,
    downloadType: 'asPDF',
  });

  const isDownloading = isDownloadingOriginal || isDownloadingHighlighted || isDownloadingAsPDF;

  const handleDownloadOriginal = () => {
    downloadOriginal();
    trackCustomEvent(TrackingEvents.DownloadDocumentInDocViewer, { caseId, docId, type: 'original' });
    setShowOptions(false);
  };

  const handleDownloadAsPDF = async () => {
    setDownloadingAsPDF(true);
    await downloadAsPDF();
    trackCustomEvent(TrackingEvents.DownloadDocumentInDocViewer, { caseId, docId, type: 'asPDF' });
    setDownloadingAsPDF(false);
    setShowOptions(false);
  };

  const handleDownloadHighlighted = async () => {
    setDownloadingHighlighted(true);
    await downloadHighlighted();
    trackCustomEvent(TrackingEvents.DownloadDocumentInDocViewer, { caseId, docId, type: 'highlighted' });
    setDownloadingHighlighted(false);
    setShowOptions(false);
  };

  const toggleOptions = () => {
    setShowOptions(!showOptions);
  };

  return (
    <div className="relative flex items-center" ref={containerRef}>
      <TooltipProvider delayDuration={0} skipDelayDuration={0}>
        <Tooltip open={showOptions ? false : undefined}>
          <TooltipTrigger asChild>
            <div
              onClick={toggleOptions}
              className={cn(
                'group flex cursor-pointer items-center rounded-lg border-2 border-blue-300 bg-blue-50 px-1.5 py-1 shadow-xl transition-all hover:border-blue-400 hover:bg-blue-100',
                isDownloading && 'cursor-not-allowed opacity-50',
              )}
              style={{ position: 'relative' }}
            >
              <Download
                className={cn(
                  'h-4 w-4 text-blue-600 transition-transform group-hover:scale-110',
                  isDownloading && 'animate-pulse',
                )}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Download</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <div
        className={cn(
          'absolute top-full left-1/2 mt-2 flex -translate-x-1/2 gap-2',
          showOptions ? 'pointer-events-auto' : 'pointer-events-none',
        )}
      >
        <button
          onClick={handleDownloadOriginal}
          disabled={isDownloadingOriginal}
          className={cn(
            'group flex items-center gap-1.5 rounded-lg bg-blue-50 px-2.5 py-1 text-[11px] font-medium whitespace-nowrap text-blue-700 shadow-md ring-1 ring-blue-100 backdrop-blur-sm transition-all duration-200 hover:bg-blue-100 hover:text-blue-800 hover:ring-blue-200',
            showOptions ? 'translate-y-0 scale-100 opacity-100' : '-translate-y-2 scale-95 opacity-0',
            isDownloadingOriginal && 'cursor-not-allowed opacity-50',
          )}
          style={{
            transitionDelay: showOptions ? '0ms' : '100ms',
          }}
        >
          <FileText className="h-3 w-3 text-blue-500" />
          <span>Original</span>
        </button>

        {isProcessed && fileExtension?.toLowerCase() !== 'pdf' && (
          <button
            onClick={handleDownloadAsPDF}
            disabled={isDownloadingAsPDF}
            className={cn(
              'group flex items-center gap-1.5 rounded-lg bg-blue-50 px-2.5 py-1 text-[11px] font-medium whitespace-nowrap text-blue-700 shadow-md ring-1 ring-blue-100 backdrop-blur-sm transition-all duration-200 hover:bg-blue-100 hover:text-blue-800 hover:ring-blue-200',
              showOptions ? 'translate-y-0 scale-100 opacity-100' : '-translate-y-2 scale-95 opacity-0',
              isDownloadingAsPDF && 'cursor-not-allowed opacity-50',
            )}
            style={{
              transitionDelay: showOptions ? '50ms' : '50ms',
            }}
          >
            {downloadingAsPDF ? (
              <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
            ) : (
              <FileCheck className="h-3 w-3 text-blue-500" />
            )}
            <span>As PDF</span>
          </button>
        )}

        <button
          onClick={handleDownloadHighlighted}
          disabled={isDownloadingHighlighted}
          className={cn(
            'group flex items-center gap-1.5 rounded-lg bg-blue-50 px-2.5 py-1 text-[11px] font-medium whitespace-nowrap text-blue-700 shadow-md ring-1 ring-blue-100 backdrop-blur-sm transition-all duration-200 hover:bg-blue-100 hover:text-blue-800 hover:ring-blue-200',
            showOptions ? 'translate-y-0 scale-100 opacity-100' : '-translate-y-2 scale-95 opacity-0',
            isDownloadingHighlighted && 'cursor-not-allowed opacity-50',
          )}
          style={{
            transitionDelay: showOptions
              ? isProcessed && fileExtension?.toLowerCase() !== 'pdf'
                ? '100ms'
                : '50ms'
              : '0ms',
          }}
        >
          {downloadingHighlighted ? (
            <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
          ) : (
            <Highlighter className="h-3 w-3 text-blue-500" />
          )}
          <span>Highlighted</span>
        </button>
      </div>
    </div>
  );
};
